//initial redux
import { put, call, take, fork, all, takeLatest, select, takeEvery } from "redux-saga/effects";
import { string, number } from "../../utils";
import { delay } from "redux-saga";
import pointsservice from "../../services/points";
import moment from "moment-timezone";
import {types as sitetreetypes} from "../../services/SiteTree/redux";
import { types as logintypes } from "../Login/redux";
import takeLeading from "../../utils/takeLeading";
import { types as menuDataTypes } from "../../components/Realtime/MenuData/redux";

const LIVE_DATA_INTERVAL = 5000;
const FAKE_VALUE_INTERVAL = 1000;
const LIVE_PAGE_CONFIG = "livePageConfig";

const initialState = {
    liveTypes: [
        {
            displayValue: 0,
            jump: 0,
            value: 0,
            units: "$",
            unitsPlacement: "before",
            unitsSize: "normal",
            subText: "Spent Today",
        },
        {
            value: 0,
            units: "kWh",
            unitsPlacement: "after",
            unitsSize: "small",
            subText: "",
        },
        {
            value: 0,
            units: "kW",
            unitsPlacement: "after",
            unitsSize: "small",
            subText: "",
        },
        {
            value: 0,
            units: "kgCO₂",
            unitsPlacement: "after",
            unitsSize: "small",
            subText: "",
        },
    ],
    selectedType: "$",
    dailyUsagePointId: null,
    currentUsagePointId: null,
    carbonImpactPointId: null,
    lineGraphConfig: [],
    onPage: false,
	isBusy: false,
	errors: [],
    hasError: false,
    rerender: 1,
    livePageConf: {}
};

/////////
//types
export const types = {
    SELECT_TYPE: "screens/live/SELECT_TYPE",
    START_COMPONENT: "screens/live/START_COMPONENT",
    STOP_COMPONENT: "screens/live/STOP_COMPONENT",
    UPDATE_LIVE_DATA: "screens/live/UPDATE_LIVE_DATA",
    LOAD_POINT_IDS: "screens/live/LOAD_POINT_IDS",
    SET_DAILY_USAGE_POINT_ID: "screens/live/SET_DAILY_USAGE_POINT_ID",
    SET_CURRENT_USAGE_POINT_ID: "screens/live/SET_CURRENT_USAGE_POINT_ID",
    SET_CARBON_IMPACT_POINT_ID: "screens/live/SET_CARBON_IMPACT_POINT_ID",
    SET_DAILY_COST: "screens/live/SET_DAILY_COST",
    SET_DAILY_USAGE: "screens/live/SET_DAILY_USAGE",
    SET_CURRENT_USAGE: "screens/live/SET_CURRENT_USAGE",
    SET_CARBON_IMPACT: "screens/live/SET_CARBON_IMPACT",
    LOAD_LINE_GRAPH_CONFIG: "screens/live/LOAD_LINE_GRAPH_CONFIG",
    SET_LINE_GRAPH_CONFIG:  "screens/live/SET_LINE_GRAPH_CONFIG",
    START_FAKER:  "screens/live/START_FAKER",
    UPDATE_FAKE_DATA:  "screens/live/UPDATE_FAKE_DATA",
    SET_INTERVAL_DAILY_COST: "screens/live/SET_INTERVAL_DAILY_COST",
    RESET_INTERVAL: "screens/live/RESET_INTERVAL",
    SET_LIVE_TYPES: "screens/live/SET_LIVE_TYPES",
    SET_LIVE_PAGE_CONFIGURATION: "screens/live/SET_LIVE_PAGE_CONFIGURATION"
};

///////////
//reducers
export const reducers = {
	reducer(state = initialState, action) {
		switch (action.type) {
            case types.SELECT_TYPE:{
                return {...state, selectedType: action.payload.units};
            }
            case types.START_COMPONENT: {
                return {...state, onPage: true};
            }
            case types.STOP_COMPONENT:{
                return {...state, onPage: false};
            }
            case types.UPDATE_LIVE_DATA:{
                return state;
            }
            case types.LOAD_POINT_IDS:{
                return state;
            }
            case types.SET_DAILY_USAGE_POINT_ID:{
                return {...state, dailyUsagePointId: action.payload.dailyUsagePointId};
            }
            case types.SET_CURRENT_USAGE_POINT_ID:{
                return {...state, currentUsagePointId: action.payload.currentUsagePointId};
            }
            case types.SET_CARBON_IMPACT_POINT_ID: {
                return {...state, carbonImpactPointId: action.payload.carbonImpactPointId};
            }
            case types.SET_INTERVAL_DAILY_COST:{
                return {...state, liveTypes: state.liveTypes.map(type => type.units === "$" ? {...type, jump: action.payload.jump, jumpDate: action.payload.jumpDate} : type)}
            }
            case types.SET_DAILY_COST:{
                return {...state, liveTypes: state.liveTypes.map(type => type.units === "$" ? {...type, value: action.payload.dailyCost, displayValue: action.payload.dailyCost !== type.value ? action.payload.dailyCost : type.displayValue} : type)}
            }
            case types.SET_DAILY_USAGE:{
                return {...state, liveTypes: state.liveTypes.map(type => type.units === "kWh" ? {...type, value: action.payload.dailyUsage} : type)}
            }
            case types.SET_CURRENT_USAGE:{
                return {...state, liveTypes: state.liveTypes.map(type => type.units === "kW" ? {...type, value: action.payload.currentUsage} : type)}
            }
            case types.SET_CARBON_IMPACT:{
                return {...state, liveTypes: state.liveTypes.map(type => type.units === "kgCO₂" ? {...type, value: action.payload.carbonImpact} : type)}
            }
            case types.LOAD_LINE_GRAPH_CONFIG:{
                return state;
            }
            case types.SET_LIVE_TYPES: {
                return {...state, liveTypes: action.payload }
            }
            case types.SET_LINE_GRAPH_CONFIG:{
                return {...state, lineGraphConfig: action.payload.lineGraphConfig, rerender: state.rerender++};
            }
            case types.SET_LIVE_PAGE_CONFIGURATION:{
                return { ...state, livePageConf: action.payload.conf };
            }
			default:
				return state;
		}
	}
};

function* loadDailyCost(){
    const currentTariff = yield select(x => x.tariffs.currentTariff);
    const currentTariffVersion = yield select(x => x.tariffs.currentTariffVersion);
    const dailyUsagePointId = yield select(x => x.live.dailyUsagePointId);
    const currentSite = yield select(x => x.sitetree.currentSite);
    if(!currentTariff || !dailyUsagePointId || !currentSite){
        yield put(actions.setDailyCost(0));
    }else{
        let utcOffset = 0;
        if(currentSite && currentSite.momentTimezone){
            utcOffset = moment().tz(currentSite.momentTimezone).utcOffset();
        }
        let fromDate = moment().utc().add(utcOffset, 'm').startOf('day');
        let toDate = 'now';
        
        const costPerTariffPeriod = yield call(pointsservice.TrendableDataGetData, dailyUsagePointId, fromDate, toDate, currentTariff.tariffPK, currentTariffVersion, "Daily", true, true);
        if(costPerTariffPeriod instanceof Error || !costPerTariffPeriod){return;}
        let total = 0;
        if(costPerTariffPeriod){
            for(var something in costPerTariffPeriod) {
                if(costPerTariffPeriod.hasOwnProperty(something)) {
                    costPerTariffPeriod[something].forEach(s => {
                        total += s.value;
                    });
                }
            }

            const liveTypes = yield select(x => x.live.liveTypes);
            let prevTotal = 0;
            let prevJumpTime = null;
            for(var l in liveTypes) {
                if(liveTypes.hasOwnProperty(l)) {
                    if(liveTypes[l].units === "$") {
                        prevTotal = liveTypes[l].value;
                        prevJumpTime = liveTypes[l].jumpDate;
                        break;
                    }
                }
            }

            total = number.parseNumber(total, 2);
            yield put(actions.setDailyCost(total));

            let difference = total - prevTotal;
            if(difference > 0.01 && difference < 0.50) {
                let jump = difference;
                let jumpDate = new Date();
                yield put(actions.setIntervalDailyCost(jump, jumpDate))
            } else {
                if(prevJumpTime == null || new Date() - prevJumpTime > 15000) {
                    yield put(actions.setIntervalDailyCost(0, null))
                }
            }
        }
    }
}

function* loadCarbonImpact(){
    let currentSite = yield select(x => x.sitetree.currentSite);
    const carbonImpactPointId = yield select(x => x.live.carbonImpactPointId);
    if(!carbonImpactPointId){
        yield put(actions.setCarbonImpact(0));
    }else{
        let utcOffset = 0;
        if (currentSite && currentSite.momentTimezone) {
            utcOffset = moment().tz(currentSite.momentTimezone).utcOffset();
        }
        let fromDate = moment().utc().add(utcOffset, 'm').startOf('day'); // start of today
        let toDate = 'now';

        const carbonImpact = yield call(
            pointsservice.TrendableDataGetDataEmissions,
            carbonImpactPointId,
            fromDate,
            toDate,
            "Daily",
            true,
            true,
            currentSite.state
         );

        if(carbonImpact instanceof Error || !carbonImpact){return;}
        let impact = 0;
        if(carbonImpact && carbonImpact.length){
            impact = carbonImpact[carbonImpact.length - 1].value;
        }
        yield put(actions.setCarbonImpact(impact));
    }
}

function getChildViewByName(flattenedSiteTree, view, name){
    let childView = null;
    if(flattenedSiteTree && name && view && view._tree && view._tree.children && view._tree.children.length){
        for(var ii = 0; ii < view._tree.children.length; ii++){
            let wkView = flattenedSiteTree[view._tree.children[ii]];
            if(wkView && wkView.name === name){
                childView = wkView;
                break;
            }
        }
    }
    return childView;
}

//////////
//sagas
export const sagas = {
	*rootSaga() {
		yield all([
            this.startComponent(), 
            this.loadPointIds(),
            this.loadLineGraphConfig(),
            this.startFaker(),
            this.updateFakeData(),
            this.setLivePageConf(),
            liveDataSaga.start()
        ]);
    },
    *startComponent(){
        yield takeLatest(types.START_COMPONENT, function*(){
            for(;;){
                const onPage = yield select(x => x.live.onPage);
                if(onPage){
                    yield put(actions.updateLiveData());
                }else{
                    break;
                }
                yield delay(LIVE_DATA_INTERVAL);
            }
        });
    },
    *startFaker() {
        yield takeLatest(types.START_FAKER, function*() {
            yield delay(5000); // Delay fake data by 5secs to let the page load.
            for(;;) {
                const onPage = yield select(x => x.live.onPage);
                if(onPage) {
                    yield put(actions.updateFakeData());
                } else {
                    break;
                }
                yield delay(FAKE_VALUE_INTERVAL);
            }
        });
    },
    *updateFakeData(){
        yield takeLatest(types.UPDATE_FAKE_DATA, function*(){
            let liveTypes = yield select(x => x.live.liveTypes);

            for(var i in liveTypes) {
                if(liveTypes.hasOwnProperty(i)) {
                    if(liveTypes[i].units === "$") {
                        let increment = liveTypes[i].jump / 15;
                        liveTypes[i].displayValue += increment;
                    }
                }
            }
        });
    },
    *loadPointIds(){
        yield takeLatest([sitetreetypes.SET_CURRENT_SITE, sitetreetypes.ON_LOAD, types.START_COMPONENT], function*(){
            const onPage = yield select(x => x.live.onPage);
            if(!onPage){
                return;
            }
            const [flattenedSiteTree, currentSite] = yield all([
                        select(s => s.sitetree.flattenedSiteTree), 
                        select(s => s.sitetree.currentSite),
                    ]);
            if(!flattenedSiteTree || !currentSite || !currentSite._tree || !currentSite._tree.children || !currentSite._tree.children.length){
                yield put(actions.setDailyCost(0));
                yield put(actions.setDailyUsage(0));
                yield put(actions.setCurrentUsage(0));
                yield put(actions.setCarbonImpact(0));
                return;
            }
            let dailyUsagePointId = null;
            let currentUsagePointId = null;
            let carbonImpactPointId = null;
            for(var ii = 0; ii < currentSite._tree.children.length; ii++){
                let wkSite = flattenedSiteTree[currentSite._tree.children[ii]];
                if(wkSite){
                    if(wkSite.name === "Meters"){
                        let rec = flattenedSiteTree.First(x => x.name === "Main" && x.parentID == wkSite.objectID);
                        if (rec != null) {
                            dailyUsagePointId = rec.objectID;
                        }
                    }else if(wkSite.name === "Meters.kW"){
                        let rec = flattenedSiteTree.First(x => x.name === "Main" && x.parentID == wkSite.objectID);
                        if (rec != null) {
                            currentUsagePointId = rec.objectID;
                        }
                    }else if(wkSite.name.includes("CO2")){
                        let rec = flattenedSiteTree.First(x => x.name === "Main" && x.parentID == wkSite.objectID);
                        if (rec != null) {
                            carbonImpactPointId = rec.objectID;
                        }
                    }
                }
            }

            yield all([
                put(actions.setDailyUsagePointId(dailyUsagePointId)),
                put(actions.setCurrentUsagePointId(currentUsagePointId)),
                put(actions.setCarbonImpactPointId(dailyUsagePointId)),
            ]);
            yield put(actions.resetInterval());
            yield put(actions.updateLiveData());
        });
    },
    *loadLineGraphConfig(){
        yield takeLatest([sitetreetypes.SET_CURRENT_SITE, sitetreetypes.ON_LOAD, types.START_COMPONENT], function*(){
            const onPage = yield select(x => x.live.onPage);
            if(!onPage){
                return;
            }
            const currentSite = yield select(x => x.sitetree.currentSite);
            const flattenedSiteTree = yield select(x => x.sitetree.flattenedSiteTree);
            
            let lineGraphConfig = [];

            let supportView = getChildViewByName(flattenedSiteTree, currentSite, "Support");
            if(supportView){
                let liveView = getChildViewByName(flattenedSiteTree, supportView, "Live");
                if(liveView){
                    let graphsView = getChildViewByName(flattenedSiteTree, liveView, "Graphs");
                    if(graphsView && graphsView._tree && graphsView._tree.children && graphsView._tree.children.length){
                        for(var ii = 0; ii < graphsView._tree.children.length; ii++){
                            let wkChild = flattenedSiteTree[graphsView._tree.children[ii]];
                            if(wkChild) {
                                lineGraphConfig.push(wkChild);
                            }
                        }
                    }
                }
            }
            lineGraphConfig.sort(function(first, second){
                return first.name > second.name ? 1 : -1;
            });
            yield put(actions.setLineGraphConfig(lineGraphConfig));
        });
    },
    *setLivePageConf() {
        const currentDetails = yield select(x => x.login.currentDetails);
        let _conf = currentDetails.config;
        if (!_conf) {
           const result = yield take([logintypes.STORE_DETAILS, logintypes.ON_LOGIN_FAILED]);
           if (result.type === logintypes.ON_LOGIN_FAILED) {
              return;
           }
           _conf = result.payload.data.config;
        }
  
        const livePageConf =  _conf[LIVE_PAGE_CONFIG] ? JSON.parse(_conf[LIVE_PAGE_CONFIG].configJson, string.toCamelCase) : {};
  
        livePageConf.fetched = true;
        yield put(actions.setLivePageConf(livePageConf));
     }   
};

const liveDataSaga = {
    *start() {
        yield all([
            this.updateDailyUsage(),
            this.updateDailyCost(),
            this.updateCurrentUsage(),
            this.updateCarbonImpact()
        ]);
    },
    *updateDailyUsage() {
        yield takeEvery(menuDataTypes.SET_DAILY_USAGE, function*({payload}) {
            yield put(actions.setDailyUsage(payload));
        });   
    },
    *updateDailyCost() {
        yield takeLeading(types.UPDATE_LIVE_DATA, loadDailyCost);
    },
    *updateCurrentUsage() {
        yield takeEvery(menuDataTypes.SET_CURRENT_USAGE, function*({payload}) {
            yield put(actions.setCurrentUsage(payload));
        });
    },
    *updateCarbonImpact() {
        yield takeLeading(types.UPDATE_LIVE_DATA, loadCarbonImpact);
    }
}

////////
//actions
export const actions = {
    setLiveTypes(payload) {
        return {
            type: types.SET_LIVE_TYPES,
            payload
        }
    },
    startComponent() {
        return {
           type: types.START_COMPONENT,
           payload: {}
        };
     },
     startFaker() {
         return {
             type: types.START_FAKER,
         }
     },
     stopComponent() {
        return {
           type: types.STOP_COMPONENT,
           payload: {}
        };
     },
    selectType(units){
        return{
            type: types.SELECT_TYPE,
            payload: {units}
        };
    },
     updateLiveData(){
         return {
             type: types.UPDATE_LIVE_DATA,
             payload: {},
         };
     },
     updateFakeData() {
         return {
             type: types.UPDATE_FAKE_DATA,
         }
     },
     loadPointIds(){
        return {
            type: types.LOAD_POINT_IDS,
            payload: {}
        };
     },
     setDailyUsagePointId(dailyUsagePointId){
        return {
            type: types.SET_DAILY_USAGE_POINT_ID,
            payload: {dailyUsagePointId}
        };
     },
     setCurrentUsagePointId(currentUsagePointId){
        return {
            type: types.SET_CURRENT_USAGE_POINT_ID,
            payload: {currentUsagePointId}
        };
     },
     setCarbonImpactPointId(carbonImpactPointId){
        return {
            type: types.SET_CARBON_IMPACT_POINT_ID,
            payload: {carbonImpactPointId}
        };
     },
     setDailyCost(dailyCost){
         return {
             type: types.SET_DAILY_COST,
             payload: {dailyCost}
         };
     },
     setDailyUsage(dailyUsage){
         return {
             type: types.SET_DAILY_USAGE,
             payload: {dailyUsage}
         };
     },
     setCurrentUsage(currentUsage){
         return {
             type: types.SET_CURRENT_USAGE,
             payload: {currentUsage}
         };
     },
     setCarbonImpact(carbonImpact){
         return {
             type: types.SET_CARBON_IMPACT,
             payload: {carbonImpact}
         };
     },
     setIntervalDailyCost(jump, jumpDate) {
        return {
            type: types.SET_INTERVAL_DAILY_COST,
            payload: {jump, jumpDate}
        }
     },
     loadLineGraphConfig(){
         return {
             type: types.LOAD_LINE_GRAPH_CONFIG,
             payload: {}
         };
     },
     resetInterval() {
        return {
            type: types.RESET_INTERVAL,
        }
     },
     setLineGraphConfig(lineGraphConfig){
         return {
             type: types.SET_LINE_GRAPH_CONFIG,
             payload: {lineGraphConfig}
         };
     },
     setLivePageConf(conf) {
        return {
           type: types.SET_LIVE_PAGE_CONFIGURATION,
           payload: { conf }
        };
     },
};



// WEBPACK FOOTER //
// ./src/ui/Live/redux.js